import { StyledSelect } from "../../primitives/styled-select";
import type * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { MessageSquare } from "lucide-react";
import { cn } from "../../lib/cn";
import type {
  ActionFieldError,
  ActionFieldSpec,
  ActionFieldValue,
} from "../ActionFieldEditor";
import {
  ENVELOPE_CLASSES,
  EnvelopeHeader,
  asString,
  specOfName,
  useEnvelopeCommit,
} from "./envelope-primitives";
import {
  InlineAIRefiner,
  type RefineSubmitInput,
  type RefineSubmitResult,
  type RefineSubmitWithDiff,
} from "./InlineAIRefiner";

/**
 * SendMessagePreview — envelope-style HITL editor for ``send_message``
 * proposals (Teams / Slack / Google Chat / WhatsApp) — May 26, 2026.
 *
 * Mirrors :class:`EmailSendDraftPreview` shape so the operator experience
 * stays homogeneous across the "compose-and-send" action family:
 *
 * 1. Channel picker as the visual anchor (``In:`` header — same role as
 *    ``A:`` in the email envelope; the BE schema synthesises it as a TEXT
 *    field today, will swap to SELECT once we expose per-vendor channel
 *    discovery — the picker degrades gracefully either way).
 * 2. A single editable message body — the focus of the card.
 * 3. The body is wrapped in :class:`InlineAIRefiner` so selecting any
 *    substring surfaces the floating "Migliora / Più formale / Più breve
 *    / Più lungo / Chiedi all'AI…" bubble (Notion-style). The endpoint
 *    rewrites ONLY the selection — surrounding text is preserved verbatim.
 *
 * Like the email envelope, ``previewOwnedFields`` removes ``channel_name``
 * + ``message`` from the stacked ``ActionFieldEditor`` so the operator
 * never sees the same input twice. Any additional action-time field the
 * BE adds (e.g. ``thread_id``, ``mention_users``) still renders in the
 * stacked editor below.
 */

export const MESSAGE_PREVIEW_OWNED_FIELDS = [
  "channel_name",
  "message",
] as const;

type MessageFieldName = (typeof MESSAGE_PREVIEW_OWNED_FIELDS)[number];

export interface SendMessagePreviewProps {
  /** Proposal id — required for the inline AI refiner (refine-field BE). */
  readonly proposalId?: string;
  /** Host-supplied transport for the inline AI refiner; see
   * :class:`EmailSendDraftPreviewProps.refineSubmit` for the rationale. */
  readonly refineSubmit?: (input: RefineSubmitInput) => Promise<RefineSubmitResult>;
  /** OPTIONAL diff-mode transport (Jul 2026) — Apple-style inline redline for
   * the body rewrite. Takes precedence over ``refineSubmit`` when present;
   * ``refineSubmit`` remains the replace-in-place fallback. */
  readonly refineSubmitWithDiff?: RefineSubmitWithDiff;
  /** Live field values (proposed merged with edits). */
  readonly proposedFields: Readonly<Record<string, ActionFieldValue>>;
  /** When provided (post-execution), draws the executed copy. */
  readonly executedFields?: Readonly<Record<string, ActionFieldValue>>;
  /** Controlled API — present when the proposal is still editable. */
  readonly schema?: readonly ActionFieldSpec[];
  readonly values?: Readonly<Record<string, ActionFieldValue>>;
  readonly errors?: Readonly<Record<string, ActionFieldError | undefined>>;
  readonly onChange?: (next: Record<string, ActionFieldValue>) => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

// Field constants + Row primitives + helpers live in ``./envelope-primitives``
// so this preview is visually & behaviourally indistinguishable from
// EmailSendDraftPreview beyond the labels + icon (DRY consolidation —
// May 26, 2026, see envelope-primitives.tsx header).

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SendMessagePreview: React.FC<SendMessagePreviewProps> = ({
  proposalId,
  refineSubmit,
  refineSubmitWithDiff,
  proposedFields,
  executedFields,
  schema,
  values,
  errors,
  onChange,
  disabled,
  className,
}) => {
  const { i18n } = useLingui();
  const isControlled = Boolean(values && onChange);
  const display = executedFields ?? proposedFields;
  const liveValues = values ?? display;

  const channelSpec = specOfName(schema, "channel_name");
  const messageSpec = specOfName(schema, "message");

  const commit = useEnvelopeCommit<MessageFieldName>(values, onChange);

  // ---------------------------------------------------------------------
  // Read-only fallback — post-execution snapshot.
  // ---------------------------------------------------------------------
  if (!isControlled) {
    const channel = asString(display.channel_name);
    const message = asString(display.message);

    return (
      <div className={cn("flex flex-col gap-2 text-sm", className)}>
        <EnvelopeHeader
          icon={<MessageSquare className="h-3.5 w-3.5" />}
          label={i18n._(msg`Messaggio`)}
        />
        <div className="flex flex-col rounded-lg border border-hairline bg-white/60">
          <div className="flex items-baseline gap-2 border-hairline border-b px-3 py-2">
            <span className="shrink-0 font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]">
              {i18n._(msg`In`)}
            </span>
            <span className="truncate text-[12.5px] text-ink-1">{channel || "—"}</span>
          </div>
          <div className="whitespace-pre-wrap px-3 py-2 text-[12.5px] text-ink-1 leading-relaxed">
            {message || (
              <span className="text-ink-3 italic">
                {i18n._(msg`Messaggio vuoto.`)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // Controlled — inline envelope editor + AI refiner on the body.
  // ---------------------------------------------------------------------
  const channelValue = asString(liveValues.channel_name);
  const messageValue = asString(liveValues.message);
  const channelOptions = channelSpec?.options ?? [];

  // The InlineAIRefiner requires a proposal id AND a transport — when
  // either is missing (Storybook, preview-only mounts, refine API
  // unavailable) we degrade gracefully to a plain textarea.
  const refinerEnabled =
    Boolean(proposalId && (refineSubmit || refineSubmitWithDiff)) && !disabled;

  const bodyTextarea = (
    refinerProps: {
      readonly textareaRef: React.RefObject<HTMLTextAreaElement | null>;
      readonly onSelect: React.ReactEventHandler<HTMLTextAreaElement>;
    } | null,
  ) => (
    <textarea
      ref={refinerProps?.textareaRef}
      className="min-h-[140px] w-full resize-y bg-transparent px-3 py-2 text-[12.5px] text-ink-1 leading-relaxed placeholder:text-ink-3 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
      value={messageValue}
      onChange={(e) => commit("message", e.target.value)}
      onSelect={refinerProps?.onSelect}
      onMouseUp={refinerProps?.onSelect}
      onKeyUp={refinerProps?.onSelect}
      disabled={disabled || messageSpec?.readonly}
      placeholder={i18n._(msg`Scrivi qui il messaggio…`)}
      aria-label={i18n._(msg`Messaggio`)}
    />
  );

  return (
    <div className={cn("flex flex-col gap-2 text-sm", className)}>
      <EnvelopeHeader
        icon={<MessageSquare className="h-3.5 w-3.5" />}
        label={i18n._(msg`Messaggio`)}
      />

      {/* ONE unified sheet — channel/recipient header row · message body,
          hairline-divided, sharing a single border + focus-ring (mirrors the
          Conversazioni reply composer). */}
      <div className="flex flex-col rounded-lg border border-hairline bg-white/60 transition-colors focus-within:border-ando-orange/30 focus-within:ring-2 focus-within:ring-ando-orange/50">
        {/* Channel / recipient */}
        <div className="border-hairline border-b px-3 py-2">
          <div className="flex items-baseline gap-2">
            <span className="shrink-0 font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]">
              {i18n._(msg`In`)}
            </span>
            {channelOptions.length > 0 ? (
              <StyledSelect
                className="w-full bg-transparent text-[12.5px] text-ink-1 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                value={channelValue}
                onChange={(e) => commit("channel_name", e.target.value)}
                disabled={disabled || channelSpec?.readonly}
                aria-label={i18n._(msg`Canale / destinatario`)}
              >
                <option value="">{i18n._(msg`— Seleziona —`)}</option>
                {channelOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </StyledSelect>
            ) : (
              <input
                type="text"
                className="w-full bg-transparent text-[12.5px] text-ink-1 placeholder:text-ink-3 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                value={channelValue}
                onChange={(e) => commit("channel_name", e.target.value)}
                disabled={disabled || channelSpec?.readonly}
                placeholder={i18n._(msg`#general / Mario Rossi / +39…`)}
                aria-label={i18n._(msg`Canale / destinatario`)}
              />
            )}
          </div>
          {errors?.channel_name ? (
            <p className={ENVELOPE_CLASSES.err}>{errors.channel_name.message}</p>
          ) : channelSpec?.helperText ? (
            <p className={ENVELOPE_CLASSES.help}>{channelSpec.helperText}</p>
          ) : null}
        </div>

        {/* Message body — bare textarea living inside the sheet */}
        <div>
          {refinerEnabled && proposalId && (refineSubmit || refineSubmitWithDiff) ? (
            <InlineAIRefiner
              proposalId={proposalId}
              fieldName="message"
              value={messageValue}
              onChange={(v) => commit("message", v)}
              disabled={disabled || messageSpec?.readonly}
              submit={refineSubmit}
              submitWithDiff={refineSubmitWithDiff}
            >
              {(rp) => bodyTextarea(rp)}
            </InlineAIRefiner>
          ) : (
            bodyTextarea(null)
          )}
          {errors?.message ? (
            <p className="px-3 pb-2 text-[11px] text-red-600">
              {errors.message.message}
            </p>
          ) : messageSpec?.helperText ? (
            <p className="px-3 pb-2 text-[11px] text-ink-3">{messageSpec.helperText}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};
SendMessagePreview.displayName = "SendMessagePreview";
